<script setup lang="ts">
/**
 * The chat's live line (MAR-1020, reworked in MAR-1057): what Marcus is doing while the user waits.
 *
 * `ChatMessages` mounts it for the whole time a streaming turn waits for its answer — from send, after
 * text written before a tool call, after each tool result, and in a resume turn — and drops it at the
 * first piece of the answer. Two faces:
 *
 *   - THINKING (no `activity`): a small mark and a caption. The caption starts at "Pensando…" and
 *     rotates through the locale's thinking verbs; a screen reader hears one steady label instead. It
 *     says nothing about WHAT the model thinks: the thought summaries are Gemini's raw English
 *     reasoning, prompt internals included, so the server sends no thought text at all (Alex,
 *     2026-10-02). The captions are verbs of thinking only, never an action Marcus may not be taking.
 *   - A RUNNING TOOL (`activity`): the tool's label (it carries the search query when there is one),
 *     the research question as a one-line detail, and the seconds it has run once past ~5 s.
 *
 * The mark cycles scales → seal → pen with the caption, each shape keeping its own motion while it
 * shows (draft: https://claude.ai/artifact/TGdKuvsv1gqfqhDA6YqwYv, "Cycle"). The seal is `MarcusMark`
 * without its `animated` sheen, which ignores reduced motion. Under `prefers-reduced-motion` nothing
 * rotates or moves and the mark stays on the scales. The mark is in the caption's ink
 * (`muted-foreground`), not the accent, which stays for things you can click; and since the transcript
 * has no avatars, it is the only Marcus mark on screen while Marcus works (Alex, 2026-10-06).
 *
 * No watcher (`apps/web/AGENTS.md`, "Vue reactivity"): two intervals started in `onMounted` and
 * cleared in `onUnmounted`, and `computed` for everything shown.
 */
import MarcusMark from '~/components/MarcusMark.vue'

const props = defineProps<{
  activity?: { label: string; detail?: string; startedAt: number } | null
}>()

const { t } = useI18n()

const CAPTION_KEYS = [
  'webChat.thinking',
  'webChat.thinkingDeliberating',
  'webChat.thinkingConnecting',
  'webChat.thinkingWeighing',
  'webChat.thinkingMarcusing',
] as const
const SHAPES = ['scales', 'seal', 'pen'] as const
const TICK_MS = 2400
const ELAPSED_AFTER_S = 5

const captionIndex = ref(0)
const shapeIndex = ref(0)
const now = ref(Date.now())
let rotation: ReturnType<typeof setInterval> | undefined
let clock: ReturnType<typeof setInterval> | undefined

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Any caption after the first, at random, never the same one twice in a row. */
function nextCaption() {
  let next = captionIndex.value
  while (next === captionIndex.value) next = 1 + Math.floor(Math.random() * (CAPTION_KEYS.length - 1))
  captionIndex.value = next
}

onMounted(() => {
  clock = setInterval(() => { now.value = Date.now() }, 1000)
  if (prefersReducedMotion()) return
  rotation = setInterval(() => {
    nextCaption()
    shapeIndex.value = (shapeIndex.value + 1) % SHAPES.length
  }, TICK_MS)
})

onUnmounted(() => {
  clearInterval(rotation)
  clearInterval(clock)
})

const shape = computed(() => SHAPES[shapeIndex.value])
const caption = computed(() => t(CAPTION_KEYS[captionIndex.value]!))
const elapsedSeconds = computed(() =>
  props.activity ? Math.max(0, Math.floor((now.value - props.activity.startedAt) / 1000)) : 0,
)
const showElapsed = computed(() => !!props.activity && elapsedSeconds.value >= ELAPSED_AFTER_S)
</script>

<template>
  <div
    class="mt-1 flex min-w-0 items-start gap-2 text-xs font-medium text-muted-foreground"
    role="status"
    aria-live="polite"
    data-testid="chat-thinking"
  >
    <span class="live-mark relative mt-px h-4 w-4 shrink-0 text-muted-foreground" :data-shape="shape" aria-hidden="true">
      <svg
        class="mark mark-scales"
        :class="{ on: shape === 'scales' }"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.6"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M12 4v16M8 20h8" />
        <g class="beam">
          <path d="M4 6h16" />
          <g class="pan-l"><path d="M4 6l-2.4 6h4.8z" /></g>
          <g class="pan-r"><path d="M20 6l-2.4 6h4.8z" /></g>
        </g>
      </svg>
      <MarcusMark class="mark mark-seal" :class="{ on: shape === 'seal' }" />
      <svg
        class="mark mark-pen"
        :class="{ on: shape === 'pen' }"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path class="stroke" d="M2 15c2-4 3.5-4 4 0s2 4 3.5 0 2.5-4 4 0 2 4 3.5 0 2-3 5-1" />
        <path d="M3 20h18" opacity=".35" />
      </svg>
    </span>

    <span class="flex min-w-0 flex-1 flex-col">
      <template v-if="activity">
        <span class="flex min-w-0 items-baseline gap-1.5">
          <span class="truncate">{{ activity.label }}</span>
          <span
            v-if="showElapsed"
            class="shrink-0 tabular-nums text-muted-foreground/70"
            aria-hidden="true"
            data-testid="chat-live-elapsed"
          >{{ t('webChat.elapsedSeconds', { seconds: elapsedSeconds }) }}</span>
        </span>
        <span
          v-if="activity.detail"
          class="block max-w-full truncate font-normal text-muted-foreground/80"
          data-testid="chat-live-detail"
        >{{ activity.detail }}</span>
      </template>
      <template v-else>
        <span class="sr-only" data-testid="chat-thinking-label">{{ t('webChat.thinking') }}</span>
        <span class="caption truncate" aria-hidden="true" data-testid="chat-thinking-caption">{{ caption }}</span>
      </template>
    </span>
  </div>
</template>

<style scoped>
/* The three marks share one slot; the active one turns in, the others are turned out. */
.mark {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transform: rotate(-60deg) scale(0.4);
  transition: opacity 0.5s ease, transform 0.6s cubic-bezier(0.3, 1.4, 0.5, 1);
}
.mark.on {
  opacity: 1;
  transform: none;
}

/* Scales: the beam tips side to side and the pans follow. */
.mark-scales .beam { transform-origin: 12px 6px; animation: tip 2.4s ease-in-out infinite; }
.mark-scales .pan-l { animation: pan-l 2.4s ease-in-out infinite; }
.mark-scales .pan-r { animation: pan-r 2.4s ease-in-out infinite; }
@keyframes tip { 0%, 100% { transform: rotate(-9deg); } 50% { transform: rotate(9deg); } }
@keyframes pan-l { 0%, 100% { transform: translateY(1.4px); } 50% { transform: translateY(-1.4px); } }
@keyframes pan-r { 0%, 100% { transform: translateY(-1.4px); } 50% { transform: translateY(1.4px); } }

/* Seal: breathes while it shows. */
.mark-seal.on { animation: swell 2.4s ease-in-out infinite; }
@keyframes swell { 0%, 100% { transform: scale(0.86); } 50% { transform: scale(1.04); } }

/* Pen: a line of handwriting is written, then lifted. */
.mark-pen .stroke { stroke-dasharray: 40; stroke-dashoffset: 40; animation: write 2.4s ease-in-out infinite; }
@keyframes write {
  0% { stroke-dashoffset: 40; opacity: 1; }
  60% { stroke-dashoffset: 0; opacity: 1; }
  85% { stroke-dashoffset: 0; opacity: 0; }
  100% { stroke-dashoffset: 40; opacity: 0; }
}

.caption { animation: breathe 2.4s ease-in-out infinite; }
@keyframes breathe { 0%, 100% { opacity: 0.62; } 50% { opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .mark, .mark *, .caption { animation: none !important; transition: none !important; }
  .mark-pen .stroke { stroke-dashoffset: 0; }
}
</style>
